import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { CallLogModal } from "../components/common/CallLogModal";
import { CallHistoryModal } from "../components/common/CallHistoryModal";
import { HistoryButton } from "../components/common/CustomerHistoryModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate } from "../lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Phone,
  MessageCircle,
  Eye,
  ArrowLeft,
  Filter,
  X,
  MoreVertical,
  Calendar as CalendarIcon,
  AlertCircle,
  CalendarPlus,
  MessageSquare,
  ChevronDown,
  Building2,
  User,
  RefreshCw
} from "lucide-react";

// Category badge styles
const getCategoryStyle = (category) => {
  const styles = {
    due: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Due" },
    overdue: { bg: "bg-orange-100", text: "text-orange-800", label: "Overdue" },
    delayed: { bg: "bg-blue-100", text: "text-blue-800", label: "Delayed" },
    no_show: { bg: "bg-red-100", text: "text-red-800", label: "No Show" },
    follow_up: { bg: "bg-purple-100", text: "text-purple-800", label: "Follow-up" },
    booked: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Booked" },
    new_lead: { bg: "bg-green-100", text: "text-green-800", label: "New Lead" },
    complaint: { bg: "bg-red-100", text: "text-red-800", label: "Complaint" },
    active: { bg: "bg-green-100", text: "text-green-800", label: "Active" }
  };
  return styles[category] || { bg: "bg-gray-100", text: "text-gray-800", label: category || "-" };
};

// Booking status badge
const getBookingStatusStyle = (status) => {
  if (!status) return null;
  const styles = {
    scheduled: { bg: "bg-blue-100", text: "text-blue-800" },
    completed: { bg: "bg-green-100", text: "text-green-800" },
    cancelled: { bg: "bg-gray-100", text: "text-gray-800" },
    no_show: { bg: "bg-red-100", text: "text-red-800" }
  };
  return styles[status] || { bg: "bg-gray-100", text: "text-gray-800" };
};

// Generic Customer List Component for Due, Overdue, Delayed, No Show
export const CustomerStatusPage = ({ status, title, description }) => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cres, setCres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLogContact, setCallLogContact] = useState(null);
  const [historyContact, setHistoryContact] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    branch: "all",
    cre: "all",
    category: "all",
    bookingStatus: "all",
    dateFrom: null,
    dateTo: null
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [status]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch customers
      let url = `/customers?status=${status}`;
      if (!isAdmin() && user?.id) {
        url += `&cre_user_id=${user.id}`;
      }
      const customersData = await apiRequest(url);
      
      // Fetch branches and CREs for filters (Admin only)
      let branchesData = [];
      let cresData = [];
      
      if (isAdmin()) {
        [branchesData, cresData] = await Promise.all([
          apiRequest("/branches"),
          apiRequest("/users?role=cre")
        ]);
      } else {
        branchesData = await apiRequest("/branches");
      }

      setCustomers(customersData);
      setBranches(branchesData);
      setCres(cresData);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          customer.name?.toLowerCase().includes(searchLower) ||
          customer.phone?.includes(searchLower) ||
          customer.vehicle?.toLowerCase().includes(searchLower) ||
          customer.vehicle_number?.toLowerCase().includes(searchLower) ||
          customer.last_remark?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Branch filter
      if (filters.branch !== "all" && customer.branch_id !== filters.branch) {
        return false;
      }

      // CRE filter
      if (filters.cre !== "all" && customer.assigned_cre_id !== filters.cre) {
        return false;
      }

      // Date range filter (based on last_service_date or next_due_date)
      if (filters.dateFrom || filters.dateTo) {
        const customerDate = new Date(customer.last_service_date || customer.next_due_date);
        if (filters.dateFrom && customerDate < filters.dateFrom) return false;
        if (filters.dateTo && customerDate > filters.dateTo) return false;
      }

      return true;
    });
  }, [customers, filters]);

  // Get branch name
  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || "-";
  };

  // Get CRE name
  const getCreName = (creId) => {
    const cre = cres.find(c => c.id === creId);
    return cre?.name || "-";
  };

  const handleCall = (customer, skipDialer = false) => {
    if (!skipDialer && customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
    setTimeout(() => {
      setCallLogContact({
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        status: customer.status,
        type: "customer",
        branch_id: customer.branch_id
      });
    }, skipDialer ? 0 : 800);
  };

  const handleWhatsApp = (phone) => {
    const cleaned = phone?.replace(/\D/g, "") || "";
    window.open(`https://wa.me/91${cleaned}`, "_blank");
  };

  const handleBookService = (customer) => {
    navigate("/bookings", { state: { prefillCustomer: customer } });
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      branch: "all",
      cre: "all",
      category: "all",
      bookingStatus: "all",
      dateFrom: null,
      dateTo: null
    });
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === "search") return value !== "";
    if (key === "dateFrom" || key === "dateTo") return value !== null;
    return value !== "all";
  }).length;

  // Get the category display for a customer based on their lifecycle
  const getCustomerCategory = (customer) => {
    // If source is lead (converted from Meta/Field), show "New Lead"
    if (customer.source_type === "lead") {
      return "new_lead";
    }
    // Otherwise show their SDR status
    return customer.status || status;
  };

  return (
    <div className="space-y-4" data-testid={`${status}-customers-page`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden"
            onClick={() => navigate("/calls")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                {title}
              </h1>
              <Badge 
                className="bg-[#ED1C24] text-white text-sm font-bold px-3 py-1"
                data-testid={`${status}-total-count`}
              >
                {loading ? "..." : customers.length}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm hidden md:block">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-9"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="h-9"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center bg-[#ED1C24] text-white">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bento-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-sm">Filters</span>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {/* Search */}
              <div className="col-span-2">
                <Input
                  placeholder="Search name, phone, vehicle..."
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  className="h-9"
                />
              </div>

              {/* Date From */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 justify-start text-left font-normal">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {filters.dateFrom ? format(filters.dateFrom, "dd MMM") : "From Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[200]" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateFrom}
                    onSelect={(date) => setFilters(f => ({ ...f, dateFrom: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Date To */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 justify-start text-left font-normal">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {filters.dateTo ? format(filters.dateTo, "dd MMM") : "To Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[200]" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateTo}
                    onSelect={(date) => setFilters(f => ({ ...f, dateTo: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Branch Filter */}
              <Select value={filters.branch} onValueChange={(v) => setFilters(f => ({ ...f, branch: v }))}>
                <SelectTrigger className="h-9">
                  <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* CRE Filter (Admin only) */}
              {isAdmin() && (
                <Select value={filters.cre} onValueChange={(v) => setFilters(f => ({ ...f, cre: v }))}>
                  <SelectTrigger className="h-9">
                    <User className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="CRE" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="all">All CREs</SelectItem>
                    {cres.map(cre => (
                      <SelectItem key={cre.id} value={cre.id}>{cre.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results count */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {filteredCustomers.length === customers.length 
            ? `${filteredCustomers.length} customers` 
            : `${filteredCustomers.length} of ${customers.length} customers`}
        </span>
      </div>

      {/* Desktop Table - Excel-like format */}
      <Card className="bento-card hidden md:block">
        <CardContent className="p-0">
          <div className="data-table-container overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[90px]">Date</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide min-w-[140px]">Name</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[120px]">Number</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[120px]">Vehicle</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[100px]">RG NOM</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[90px]">Call Date</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[90px]">Category</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[100px]">Branch</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide min-w-[150px]">Last Remark</TableHead>
                  {isAdmin() && <TableHead className="font-semibold text-xs uppercase tracking-wide w-[100px]">CRE</TableHead>}
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[80px]">Booking</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wide w-[180px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 12 : 11} className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ED1C24] mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 12 : 11} className="text-center py-8 text-muted-foreground">
                      No {status.replace("_", " ")} customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => {
                    const categoryStyle = getCategoryStyle(getCustomerCategory(customer));
                    return (
                      <TableRow 
                        key={customer.id} 
                        className="hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        data-testid={`${status}-row-${customer.id}`}
                      >
                        {/* DATE */}
                        <TableCell className="text-xs">
                          {customer.last_service_date ? formatDate(customer.last_service_date) : "-"}
                        </TableCell>
                        
                        {/* NAME */}
                        <TableCell className="font-medium">
                          <span className="truncate block max-w-[140px]">{customer.name}</span>
                        </TableCell>
                        
                        {/* NUMBER */}
                        <TableCell className="font-mono text-sm">
                          {formatPhone(customer.phone)}
                        </TableCell>
                        
                        {/* VEHICLE */}
                        <TableCell className="text-sm">
                          <span className="truncate block max-w-[120px]">{customer.vehicle || "-"}</span>
                        </TableCell>
                        
                        {/* RG NOM */}
                        <TableCell className="font-mono text-xs">
                          {customer.vehicle_number ? (
                            <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                              {customer.vehicle_number}
                            </span>
                          ) : "-"}
                        </TableCell>
                        
                        {/* CALL DATE (Next Follow-up) */}
                        <TableCell className="text-xs">
                          {customer.next_due_date ? formatDate(customer.next_due_date) : "-"}
                        </TableCell>
                        
                        {/* CATEGORY */}
                        <TableCell>
                          <Badge className={`${categoryStyle.bg} ${categoryStyle.text} text-[10px]`}>
                            {categoryStyle.label}
                          </Badge>
                        </TableCell>
                        
                        {/* BRANCH */}
                        <TableCell className="text-xs">
                          <span className="truncate block max-w-[100px]">{getBranchName(customer.branch_id)}</span>
                        </TableCell>
                        
                        {/* LAST REMARK */}
                        <TableCell className="text-xs text-muted-foreground">
                          <span className="truncate block max-w-[150px] italic">
                            {customer.last_remark || customer.notes || "-"}
                          </span>
                        </TableCell>
                        
                        {/* CRE NAME (Admin only) */}
                        {isAdmin() && (
                          <TableCell className="text-xs">
                            <span className="truncate block max-w-[100px]">{getCreName(customer.assigned_cre_id)}</span>
                          </TableCell>
                        )}
                        
                        {/* BOOKING STATUS */}
                        <TableCell>
                          {customer.booking_status ? (
                            <Badge className={`${getBookingStatusStyle(customer.booking_status)?.bg} ${getBookingStatusStyle(customer.booking_status)?.text} text-[10px]`}>
                              {customer.booking_status}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        
                        {/* ACTIONS */}
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {/* Call */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:bg-green-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCall(customer);
                              }}
                              data-testid={`call-btn-${customer.id}`}
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                            
                            {/* WhatsApp */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:bg-green-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWhatsApp(customer.phone);
                              }}
                              data-testid={`wa-btn-${customer.id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                            
                            {/* History */}
                            <HistoryButton 
                              customerId={customer.id} 
                              customerName={customer.name}
                            />
                            
                            {/* More Actions Dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => navigate(`/customers/${customer.id}`)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleCall(customer, true)}>
                                  <MessageSquare className="w-4 h-4 mr-2" />
                                  Add Remark
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleBookService(customer)}>
                                  <CalendarPlus className="w-4 h-4 mr-2" />
                                  Book Service
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate("/complaints", { state: { prefillCustomer: customer } })}>
                                  <AlertCircle className="w-4 h-4 mr-2" />
                                  Log Complaint
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View - Optimized for calling workflow */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ED1C24] mx-auto"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No {status.replace("_", " ")} customers found
          </div>
        ) : (
          filteredCustomers.map((customer) => {
            const categoryStyle = getCategoryStyle(getCustomerCategory(customer));
            return (
              <Card 
                key={customer.id} 
                className="bento-card"
                data-testid={`${status}-card-${customer.id}`}
              >
                <CardContent className="p-4">
                  {/* Top Row: Name, Category, Branch */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0" onClick={() => navigate(`/customers/${customer.id}`)}>
                      <p className="font-medium truncate">{customer.name}</p>
                      <p className="text-sm text-muted-foreground font-mono">{formatPhone(customer.phone)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-2">
                      <Badge className={`${categoryStyle.bg} ${categoryStyle.text} text-[10px]`}>
                        {categoryStyle.label}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{getBranchName(customer.branch_id)}</span>
                    </div>
                  </div>
                  
                  {/* Vehicle Info Row */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                    {customer.vehicle && (
                      <span className="truncate">{customer.vehicle}</span>
                    )}
                    {customer.vehicle_number && (
                      <span className="font-mono bg-gray-100 px-1 rounded">{customer.vehicle_number}</span>
                    )}
                  </div>

                  {/* Date Info Row */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span>Last: {customer.last_service_date ? formatDate(customer.last_service_date) : "-"}</span>
                    <span>Call: {customer.next_due_date ? formatDate(customer.next_due_date) : "-"}</span>
                  </div>
                  
                  {/* Last Remark */}
                  {(customer.last_remark || customer.notes) && (
                    <p className="text-xs text-muted-foreground truncate mb-3 italic border-l-2 border-gray-200 pl-2">
                      "{customer.last_remark || customer.notes}"
                    </p>
                  )}
                  
                  {/* Action Buttons - Large touch targets for calling workflow */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="default"
                      className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleCall(customer)}
                      data-testid={`${status}-mobile-call-${customer.id}`}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call
                    </Button>
                    <Button
                      size="default"
                      variant="outline"
                      className="h-12 w-12 text-green-600 border-green-200"
                      onClick={() => handleWhatsApp(customer.phone)}
                    >
                      <MessageCircle className="w-5 h-5" />
                    </Button>
                    <HistoryButton 
                      customerId={customer.id} 
                      customerName={customer.name}
                      size="default"
                    />
                    
                    {/* More Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="default"
                          variant="outline"
                          className="h-12 w-12"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => navigate(`/customers/${customer.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCall(customer, true)}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Add Remark
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleBookService(customer)}>
                          <CalendarPlus className="w-4 h-4 mr-2" />
                          Book Service
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/complaints", { state: { prefillCustomer: customer } })}>
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Log Complaint
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        open={!!callLogContact}
        onClose={() => setCallLogContact(null)}
        contact={callLogContact}
        onSuccess={fetchData}
      />

      {/* Call History Modal */}
      <CallHistoryModal
        open={!!historyContact}
        onClose={() => setHistoryContact(null)}
        contactId={historyContact?.id}
        contactType={historyContact?.type}
        contactName={historyContact?.name}
      />
    </div>
  );
};

// Specific page exports
export const DueCustomersPage = () => (
  <CustomerStatusPage
    status="due"
    title="Due Customers"
    description="Customers due for their next service (within service window)"
  />
);

export const OverdueCustomersPage = () => (
  <CustomerStatusPage
    status="overdue"
    title="Overdue Customers"
    description="Customers who have passed their due date"
  />
);

export const DelayedCustomersPage = () => (
  <CustomerStatusPage
    status="delayed"
    title="Delayed Customers"
    description="Customers with significantly delayed service appointments"
  />
);

export const NoShowCustomersPage = () => (
  <CustomerStatusPage
    status="no_show"
    title="No Show Customers"
    description="Customers who missed their scheduled appointments"
  />
);

export default CustomerStatusPage;
